/**
 * Enhanced Code Editor with resizable, movable modal and advanced features
 */
import MjmlService from 'grapesjs-preset-mautic/dist/mjml/mjml.service';
import ContentService from 'grapesjs-preset-mautic/dist/content.service';
import ButtonCloseCommands from 'grapesjs-preset-mautic/dist/buttons/buttonClose.command';
import PlaceholderHelper from '../placeholderHelper/placeholderHelper';

class EnhancedCodeEditor {
  editor;
  opts;
  codeEditor;
  modal;
  isFullscreen = false;
  savedModalSize = null;
  targetComponent = null; // Component to jump to
  placeholderHelper = null; // Placeholder helper instance

  constructor(editor, opts = {}) {
    this.editor = editor;
    this.opts = opts;
    this.targetComponent = opts.targetComponent || null;

    this.codeEditor = this.buildCodeEditor();
    this.modal = this.buildEnhancedModal();
  }

  // Build CodeMirror instance with enhanced options
  buildCodeEditor() {
    const codeEditor = this.editor.CodeManager.getViewer('CodeMirror').clone();

    codeEditor.set({
      codeName: 'htmlmixed',
      readOnly: false,
      theme: 'hopscotch',
      autoBeautify: true,
      autoCloseTags: true,
      autoCloseBrackets: true,
      lineWrapping: true,
      styleActiveLine: true,
      smartIndent: true,
      indentWithTabs: true,
      lineNumbers: true,
      foldGutter: true,
      gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
      matchBrackets: true,
      highlightSelectionMatches: { showToken: /\w/, annotateScrollbar: true },
      extraKeys: {
        // Search - Ctrl/Cmd+F
        'Ctrl-F': (cm) => {
          this.openSearch(cm);
          return false; // Prevent browser default
        },
        'Cmd-F': (cm) => {
          this.openSearch(cm);
          return false;
        },
        // Replace - Ctrl/Cmd+H
        'Ctrl-H': (cm) => {
          this.openReplace(cm);
          return false;
        },
        'Cmd-H': (cm) => {
          this.openReplace(cm);
          return false;
        },
        // Format - Ctrl/Cmd+Alt+F
        'Ctrl-Alt-F': (cm) => {
          this.formatCode(cm);
          return false;
        },
        'Cmd-Alt-F': (cm) => {
          this.formatCode(cm);
          return false;
        },
        // Save - Ctrl/Cmd+S
        'Ctrl-S': (cm) => {
          this.updateCode();
          return false;
        },
        'Cmd-S': (cm) => {
          this.updateCode();
          return false;
        },
        // Fullscreen - F11
        'F11': (cm) => {
          this.toggleFullscreen();
          return false;
        },
        // Exit fullscreen - Esc
        'Esc': (cm) => {
          if (this.isFullscreen) {
            this.exitFullscreen();
            return false;
          }
          // Allow default Esc behavior if not in fullscreen
          return true;
        },
      },
    });

    return codeEditor;
  }

  // Build enhanced modal with toolbar and status bar
  buildEnhancedModal() {
    const modal = document.createElement('div');
    modal.className = 'enhanced-code-editor-modal';
    modal.innerHTML = `
      <div class="code-editor-header">
        <div class="code-editor-title">
          <i class="fa fa-code"></i>
          <span>${Mautic.translate('grapesjsbuilder.sourceEditModalTitle')}</span>
        </div>
        <div class="code-editor-toolbar">
          <button class="code-editor-btn" data-action="format" title="Format Code (Ctrl+Alt+F)">
            <i class="fa fa-magic"></i>
            <span>Format</span>
          </button>
          <button class="code-editor-btn" data-action="search" title="Search (Ctrl+F)">
            <i class="fa fa-search"></i>
            <span>Find</span>
          </button>
          <button class="code-editor-btn" data-action="replace" title="Replace (Ctrl+H)">
            <i class="fa fa-exchange"></i>
            <span>Replace</span>
          </button>
          <button class="code-editor-btn" data-action="theme" title="Toggle Theme">
            <i class="fa fa-adjust"></i>
            <span>Theme</span>
          </button>
          <button class="code-editor-btn" data-action="helper" title="Placeholder Helper">
            <i class="fa fa-tags"></i>
            <span>Helper</span>
          </button>
          <button class="code-editor-btn" data-action="fullscreen" title="Fullscreen (F11)">
            <i class="fa fa-expand"></i>
            <span>Fullscreen</span>
          </button>
          <button class="code-editor-btn" data-action="help" title="Keyboard Shortcuts">
            <i class="fa fa-question-circle"></i>
            <span>Help</span>
          </button>
        </div>
        <button class="code-editor-close" data-action="close" title="Close">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <div class="code-editor-body">
        <textarea class="code-editor-textarea"></textarea>
      </div>
      <div class="code-editor-footer">
        <div class="code-editor-status">
          <span class="status-item">
            <i class="fa fa-map-marker"></i>
            <span class="cursor-position">Ln 1, Col 1</span>
          </span>
          <span class="status-item">
            <i class="fa fa-file-code-o"></i>
            <span class="file-type">MJML</span>
          </span>
          <span class="status-item">
            <i class="fa fa-info-circle"></i>
            <span class="editor-mode">Insert</span>
          </span>
        </div>
        <div class="code-editor-actions">
          <button class="code-editor-btn-primary" data-action="save">
            <i class="fa fa-check"></i>
            ${Mautic.translate('grapesjsbuilder.sourceEditBtnLabel')}
          </button>
          <button class="code-editor-btn-secondary" data-action="cancel">
            <i class="fa fa-times"></i>
            ${Mautic.translate('grapesjsbuilder.sourceCancelBtnLabel')}
          </button>
        </div>
      </div>
      <div class="code-editor-resize-handle code-editor-resize-n"></div>
      <div class="code-editor-resize-handle code-editor-resize-s"></div>
      <div class="code-editor-resize-handle code-editor-resize-e"></div>
      <div class="code-editor-resize-handle code-editor-resize-w"></div>
      <div class="code-editor-resize-handle code-editor-resize-ne"></div>
      <div class="code-editor-resize-handle code-editor-resize-nw"></div>
      <div class="code-editor-resize-handle code-editor-resize-se"></div>
      <div class="code-editor-resize-handle code-editor-resize-sw"></div>
    `;

    // Initialize CodeMirror
    const textarea = modal.querySelector('.code-editor-textarea');
    this.codeEditor.init(textarea);

    // Set up event listeners
    this.setupEventListeners(modal);
    this.setupDragging(modal);
    this.setupResizing(modal);
    this.setupCursorTracking();

    return modal;
  }

  setupEventListeners(modal) {
    // Toolbar actions
    modal.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const action = btn.getAttribute('data-action');
        this.handleAction(action);
      });
    });
  }

  setupDragging(modal) {
    const header = modal.querySelector('.code-editor-header');
    let isDragging = false;
    let currentX, currentY, initialX, initialY;

    header.addEventListener('mousedown', (e) => {
      // Only drag if clicking on header, not buttons
      if (e.target.closest('button') || e.target.closest('.code-editor-toolbar')) {
        return;
      }

      isDragging = true;

      // Remove transform before getting position
      if (modal.style.transform !== 'none') {
        modal.style.transform = 'none';
      }

      initialX = e.clientX - modal.offsetLeft;
      initialY = e.clientY - modal.offsetTop;

      modal.style.cursor = 'move';
      header.style.cursor = 'move';
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;

      e.preventDefault();
      currentX = e.clientX - initialX;
      currentY = e.clientY - initialY;

      modal.style.left = `${currentX}px`;
      modal.style.top = `${currentY}px`;
    });

    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        modal.style.cursor = '';
        header.style.cursor = '';
      }
    });
  }

  setupResizing(modal) {
    const handles = modal.querySelectorAll('.code-editor-resize-handle');
    let isResizing = false;
    let currentHandle = null;
    let startX, startY, startWidth, startHeight, startLeft, startTop;

    handles.forEach(handle => {
      handle.addEventListener('mousedown', (e) => {
        isResizing = true;
        currentHandle = handle;
        startX = e.clientX;
        startY = e.clientY;

        // Remove transform before resizing
        if (modal.style.transform !== 'none') {
          modal.style.transform = 'none';
        }

        // Remove CSS constraints that might interfere with resizing
        modal.style.maxWidth = 'none';
        modal.style.maxHeight = 'none';

        const rect = modal.getBoundingClientRect();
        startWidth = rect.width;
        startHeight = rect.height;
        startLeft = modal.offsetLeft;
        startTop = modal.offsetTop;

        e.preventDefault();
        e.stopPropagation();
      });
    });

    document.addEventListener('mousemove', (e) => {
      if (!isResizing) return;

      e.preventDefault();

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      const handleClass = currentHandle.className;

      // Enforce minimum sizes
      const minWidth = 400;
      const minHeight = 300;

      // Handle different resize directions
      if (handleClass.includes('resize-e')) {
        const newWidth = Math.max(minWidth, startWidth + dx);
        modal.style.width = `${newWidth}px`;
      }
      if (handleClass.includes('resize-w')) {
        const newWidth = Math.max(minWidth, startWidth - dx);
        if (newWidth > minWidth) {
          modal.style.width = `${newWidth}px`;
          modal.style.left = `${startLeft + dx}px`;
        }
      }
      if (handleClass.includes('resize-s')) {
        const newHeight = Math.max(minHeight, startHeight + dy);
        modal.style.height = `${newHeight}px`;
      }
      if (handleClass.includes('resize-n')) {
        const newHeight = Math.max(minHeight, startHeight - dy);
        if (newHeight > minHeight) {
          modal.style.height = `${newHeight}px`;
          modal.style.top = `${startTop + dy}px`;
        }
      }

      // Refresh CodeMirror on resize (throttled)
      if (this.codeEditor.editor) {
        if (!this.resizeTimeout) {
          this.resizeTimeout = setTimeout(() => {
            this.codeEditor.editor.refresh();
            this.resizeTimeout = null;
          }, 100);
        }
      }
    });

    document.addEventListener('mouseup', () => {
      if (isResizing) {
        isResizing = false;
        currentHandle = null;

        // Final refresh after resize complete
        if (this.codeEditor.editor) {
          this.codeEditor.editor.refresh();
        }
      }
    });
  }

  setupCursorTracking() {
    if (!this.codeEditor.editor) return;

    this.codeEditor.editor.on('cursorActivity', () => {
      const cursor = this.codeEditor.editor.getCursor();
      const positionEl = this.modal.querySelector('.cursor-position');
      if (positionEl) {
        positionEl.textContent = `Ln ${cursor.line + 1}, Col ${cursor.ch + 1}`;
      }
    });
  }

  handleAction(action) {
    const cm = this.codeEditor.editor;

    switch (action) {
      case 'format':
        this.formatCode(cm);
        break;
      case 'search':
        this.openSearch(cm);
        break;
      case 'replace':
        this.openReplace(cm);
        break;
      case 'theme':
        this.toggleTheme();
        break;
      case 'helper':
        this.showPlaceholderHelper();
        break;
      case 'fullscreen':
        this.toggleFullscreen();
        break;
      case 'help':
        this.showHelp();
        break;
      case 'save':
        this.updateCode();
        break;
      case 'cancel':
        this.close();
        break;
      case 'close':
        this.close();
        break;
    }
  }

  closeExistingDialogs() {
    // Close any existing search/replace dialogs
    const existingDialogs = this.modal.querySelectorAll('.code-editor-manual-search');
    if (existingDialogs.length > 0) {
      existingDialogs.forEach(dialog => {
        // Try to click the close button to trigger proper cleanup
        const closeBtn = dialog.querySelector('.search-btn-close');
        if (closeBtn) {
          closeBtn.click();
        } else {
          // Fallback: remove directly
          dialog.remove();
        }
      });
    }
  }

  openSearch(cm) {
    // Close any existing search/replace dialogs
    this.closeExistingDialogs();

    // Always use manual search for reliability
    this.showManualSearch(cm);

    /* Disabled CodeMirror native search attempts - use manual search instead
    try {
      if (cm.execCommand) {
        cm.execCommand('findPersistent');
        console.log('Using CodeMirror findPersistent');
        return;
      }
    } catch (e) {
      console.log('findPersistent failed:', e);
      try {
        cm.execCommand('find');
        console.log('Using CodeMirror find');
        return;
      } catch (e2) {
        console.log('find failed:', e2);
      }
    }
    */
  }

  openReplace(cm) {
    // Close any existing search/replace dialogs
    this.closeExistingDialogs();

    // Always use manual replace for reliability
    this.showManualReplace(cm);

    /* Disabled CodeMirror native replace attempts - use manual replace instead
    try {
      if (cm.execCommand) {
        cm.execCommand('replace');
        console.log('Using CodeMirror replace');
        return;
      }
    } catch (e) {
      console.log('replace failed:', e);
      try {
        cm.execCommand('replaceAll');
        console.log('Using CodeMirror replaceAll');
        return;
      } catch (e2) {
        console.log('replaceAll failed:', e2);
      }
    }
    */
  }

  showManualSearch(cm) {
    // Create a simple search dialog that works without CodeMirror addons
    const searchBox = document.createElement('div');
    searchBox.className = 'code-editor-manual-search';
    searchBox.innerHTML = `
      <div class="search-row">
        <input type="text" placeholder="Find..." class="search-input" />
        <span class="search-counter">0/0</span>
        <button class="search-btn-prev" title="Previous match">Prev</button>
        <button class="search-btn-next" title="Next match">Next</button>
        <button class="search-btn-close" title="Close search">×</button>
      </div>
      <div class="search-options">
        <button class="search-option-btn search-option-case" data-option="case" title="Match case">
          <i class="fa fa-font"></i>
          <span>Aa</span>
        </button>
        <button class="search-option-btn search-option-whole" data-option="whole" title="Match whole word">
          <i class="fa fa-text-width"></i>
          <span>Word</span>
        </button>
      </div>
    `;

    this.modal.querySelector('.code-editor-body').appendChild(searchBox);

    const input = searchBox.querySelector('.search-input');
    const counter = searchBox.querySelector('.search-counter');
    const caseSensitiveBtn = searchBox.querySelector('.search-option-case');
    const wholeWordBtn = searchBox.querySelector('.search-option-whole');

    let currentMatch = 0;
    let matches = [];
    let searchMarkers = []; // Store markers for highlighting
    let caseSensitive = false;
    let wholeWord = false;

    const clearMarkers = () => {
      // Clear previous highlights
      searchMarkers.forEach(marker => marker.clear());
      searchMarkers = [];
    };

    const updateCounter = () => {
      if (matches.length === 0) {
        counter.textContent = '0/0';
        counter.style.color = '#999';
      } else {
        counter.textContent = `${currentMatch + 1}/${matches.length}`;
        counter.style.color = '#4e9cdb';
      }
    };

    const search = () => {
      const query = input.value;
      if (!query) {
        clearMarkers();
        matches = [];
        updateCounter();
        return;
      }

      clearMarkers();

      // Get all text content
      const content = cm.getValue();
      matches = [];

      // Build search pattern based on options
      let searchPattern;
      if (wholeWord) {
        // Escape special regex characters in query
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Word boundary regex
        const flags = caseSensitive ? 'g' : 'gi';
        searchPattern = new RegExp(`\\b${escapedQuery}\\b`, flags);
      } else {
        // Regular search
        if (caseSensitive) {
          // Case-sensitive: use indexOf
          searchPattern = null; // Will use indexOf
        } else {
          // Case-insensitive: use regex
          const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          searchPattern = new RegExp(escapedQuery, 'gi');
        }
      }

      // Find all matches
      if (searchPattern) {
        // Using regex
        let match;
        while ((match = searchPattern.exec(content)) !== null) {
          const index = match.index;
          const matchedText = match[0];

          // Convert string index to CodeMirror position
          const beforeText = content.substring(0, index);
          const lines = beforeText.split('\n');
          const line = lines.length - 1;
          const ch = lines[lines.length - 1].length;

          const matchLines = matchedText.split('\n');
          const endLine = line + matchLines.length - 1;
          const endCh = matchLines.length > 1
            ? matchLines[matchLines.length - 1].length
            : ch + matchedText.length;

          matches.push({
            from: { line: line, ch: ch },
            to: { line: endLine, ch: endCh }
          });
        }
      } else {
        // Using indexOf (case-sensitive)
        let startIndex = 0;
        while (true) {
          const index = content.indexOf(query, startIndex);
          if (index === -1) break;

          // Convert string index to CodeMirror position
          const beforeText = content.substring(0, index);
          const lines = beforeText.split('\n');
          const line = lines.length - 1;
          const ch = lines[lines.length - 1].length;

          const queryLines = query.split('\n');
          const endLine = line + queryLines.length - 1;
          const endCh = queryLines.length > 1
            ? queryLines[queryLines.length - 1].length
            : ch + query.length;

          matches.push({
            from: { line: line, ch: ch },
            to: { line: endLine, ch: endCh }
          });

          startIndex = index + 1;
        }
      }

      if (matches.length > 0) {
        // Highlight ALL matches with yellow background
        matches.forEach((match, idx) => {
          const marker = cm.markText(match.from, match.to, {
            className: idx === 0 ? 'search-highlight-active' : 'search-highlight',
            clearOnEnter: false
          });
          searchMarkers.push(marker);
        });

        currentMatch = 0;
        updateCounter();
        cm.setSelection(matches[0].from, matches[0].to);
        cm.scrollIntoView(matches[0].from, 100);
      } else {
        updateCounter();
        this.showNotification('No matches found', 'info');
      }
    };

    const navigateToMatch = (index) => {
      if (matches.length === 0) return;

      currentMatch = index;
      updateCounter();

      // Update highlighting - make current match more visible
      clearMarkers();
      matches.forEach((match, idx) => {
        const marker = cm.markText(match.from, match.to, {
          className: idx === currentMatch ? 'search-highlight-active' : 'search-highlight',
          clearOnEnter: false
        });
        searchMarkers.push(marker);
      });

      cm.setSelection(matches[currentMatch].from, matches[currentMatch].to);
      cm.scrollIntoView(matches[currentMatch].from, 100);
      // Don't focus CodeMirror - keep focus on search input so Enter key continues to work
      // cm.focus(); // REMOVED - was causing focus to move to editor
    };

    const goNext = () => {
      if (matches.length === 0) {
        search();
      } else {
        // Loop back to first when reaching the end
        const nextIndex = (currentMatch + 1) % matches.length;
        navigateToMatch(nextIndex);
      }
    };

    const goPrev = () => {
      if (matches.length === 0) {
        search();
      } else {
        // Loop to last when going before first
        const prevIndex = (currentMatch - 1 + matches.length) % matches.length;
        navigateToMatch(prevIndex);
      }
    };

    // Input event - search as user types
    input.addEventListener('input', () => {
      search();
    });

    // Enter key - navigate to next match (loop through all)
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Escape') {
        clearMarkers();
        searchBox.remove();
      }
    });

    searchBox.querySelector('.search-btn-next').addEventListener('click', () => {
      goNext();
    });

    searchBox.querySelector('.search-btn-prev').addEventListener('click', () => {
      goPrev();
    });

    // Toggle buttons
    caseSensitiveBtn.addEventListener('click', () => {
      caseSensitive = !caseSensitive;
      caseSensitiveBtn.classList.toggle('active', caseSensitive);
      search(); // Re-search with new option
    });

    wholeWordBtn.addEventListener('click', () => {
      wholeWord = !wholeWord;
      wholeWordBtn.classList.toggle('active', wholeWord);
      search(); // Re-search with new option
    });

    searchBox.querySelector('.search-btn-close').addEventListener('click', () => {
      clearMarkers();
      searchBox.remove();
    });

    input.focus();

    // Store reference to clear markers when editor is closed
    this.currentSearchMarkers = searchMarkers;
  }

  showManualReplace(cm) {
    // Create a simple replace dialog that works without CodeMirror addons
    const replaceBox = document.createElement('div');
    replaceBox.className = 'code-editor-manual-search code-editor-replace-dialog';
    replaceBox.innerHTML = `
      <div class="search-row">
        <input type="text" placeholder="Find..." class="search-input" />
        <input type="text" placeholder="Replace with..." class="replace-input" />
        <span class="search-counter">0/0</span>
        <button class="search-btn-next" title="Next match">Next</button>
        <button class="search-btn-replace" title="Replace current match">Replace</button>
        <button class="search-btn-replace-all" title="Replace all matches">Replace All</button>
        <button class="search-btn-close" title="Close replace">×</button>
      </div>
      <div class="search-options">
        <button class="search-option-btn search-option-case" data-option="case" title="Match case">
          <i class="fa fa-font"></i>
          <span>Aa</span>
        </button>
        <button class="search-option-btn search-option-whole" data-option="whole" title="Match whole word">
          <i class="fa fa-text-width"></i>
          <span>Word</span>
        </button>
      </div>
    `;

    this.modal.querySelector('.code-editor-body').appendChild(replaceBox);

    const findInput = replaceBox.querySelector('.search-input');
    const replaceInput = replaceBox.querySelector('.replace-input');
    const counter = replaceBox.querySelector('.search-counter');
    const nextBtn = replaceBox.querySelector('.search-btn-next');
    const caseSensitiveBtn = replaceBox.querySelector('.search-option-case');
    const wholeWordBtn = replaceBox.querySelector('.search-option-whole');

    let replaceMarkers = [];
    let matches = [];
    let currentMatch = 0;
    let caseSensitive = false;
    let wholeWord = false;

    const clearMarkers = () => {
      replaceMarkers.forEach(marker => marker.clear());
      replaceMarkers = [];
    };

    const updateCounter = () => {
      if (matches.length === 0) {
        counter.textContent = '0/0';
        counter.style.color = '#999';
      } else {
        counter.textContent = `${currentMatch + 1}/${matches.length}`;
        counter.style.color = '#4e9cdb';
      }
    };

    const highlightMatches = () => {
      const query = findInput.value;
      if (!query) return;

      clearMarkers();
      matches = [];

      // Get all text content
      const content = cm.getValue();

      // Build search pattern based on options
      let searchPattern;
      if (wholeWord) {
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const flags = caseSensitive ? 'g' : 'gi';
        searchPattern = new RegExp(`\\b${escapedQuery}\\b`, flags);
      } else {
        if (caseSensitive) {
          searchPattern = null; // Will use indexOf
        } else {
          const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          searchPattern = new RegExp(escapedQuery, 'gi');
        }
      }

      // Find all matches
      if (searchPattern) {
        // Using regex
        let match;
        while ((match = searchPattern.exec(content)) !== null) {
          const index = match.index;
          const matchedText = match[0];

          const beforeText = content.substring(0, index);
          const lines = beforeText.split('\n');
          const line = lines.length - 1;
          const ch = lines[lines.length - 1].length;

          matches.push({
            from: { line: line, ch: ch },
            to: { line: line, ch: ch + matchedText.length }
          });
        }
      } else {
        // Using indexOf (case-sensitive)
        let startIndex = 0;
        while (true) {
          const index = content.indexOf(query, startIndex);
          if (index === -1) break;

          const beforeText = content.substring(0, index);
          const lines = beforeText.split('\n');
          const line = lines.length - 1;
          const ch = lines[lines.length - 1].length;

          matches.push({
            from: { line: line, ch: ch },
            to: { line: line, ch: ch + query.length }
          });

          startIndex = index + 1;
        }
      }

      // Highlight all matches
      matches.forEach((match, idx) => {
        const marker = cm.markText(match.from, match.to, {
          className: idx === currentMatch ? 'replace-highlight-active' : 'replace-highlight',
          clearOnEnter: false
        });
        replaceMarkers.push(marker);
      });

      updateCounter();

      if (matches.length > 0) {
        cm.scrollIntoView(matches[currentMatch].from, 100);
      }
    };

    const navigateToMatch = (index) => {
      if (matches.length === 0) return;

      currentMatch = index;
      updateCounter();

      // Update highlighting - make current match more visible
      clearMarkers();
      matches.forEach((match, idx) => {
        const marker = cm.markText(match.from, match.to, {
          className: idx === currentMatch ? 'replace-highlight-active' : 'replace-highlight',
          clearOnEnter: false
        });
        replaceMarkers.push(marker);
      });

      cm.setSelection(matches[currentMatch].from, matches[currentMatch].to);
      cm.scrollIntoView(matches[currentMatch].from, 100);
    };

    const goNext = () => {
      if (matches.length === 0) {
        highlightMatches();
      } else {
        const nextIndex = (currentMatch + 1) % matches.length;
        navigateToMatch(nextIndex);
      }
    };

    const replaceCurrentAndNext = () => {
      const query = findInput.value;
      const replacement = replaceInput.value;
      if (!query) return;

      if (matches.length === 0) {
        highlightMatches();
        if (matches.length === 0) {
          this.showNotification('No matches found', 'info');
          return;
        }
      }

      // Replace the current match
      const currentMatchObj = matches[currentMatch];
      const replaceLine = currentMatchObj.from.line;
      const replaceCol = currentMatchObj.from.ch;
      const content = cm.getValue();

      // Get the actual text at current match position
      const line = cm.getLine(currentMatchObj.from.line);
      const matchedText = line.substring(currentMatchObj.from.ch, currentMatchObj.to.ch);

      // Build the full content with replacement
      const lines = content.split('\n');
      const beforeMatch = lines[currentMatchObj.from.line].substring(0, currentMatchObj.from.ch);
      const afterMatch = lines[currentMatchObj.from.line].substring(currentMatchObj.to.ch);
      lines[currentMatchObj.from.line] = beforeMatch + replacement + afterMatch;

      cm.setValue(lines.join('\n'));

      // Calculate the position AFTER the replacement
      // We need to skip past the replaced text to avoid finding it again
      const skipUntilLine = replaceLine;
      const skipUntilCol = replaceCol + replacement.length;

      // Re-search to update matches
      clearMarkers();
      matches = [];

      // Get updated content after replacement
      const newContent = cm.getValue();

      // Build search pattern based on options (same as highlightMatches)
      let searchPattern;
      if (wholeWord) {
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const flags = caseSensitive ? 'g' : 'gi';
        searchPattern = new RegExp(`\\b${escapedQuery}\\b`, flags);
      } else {
        if (caseSensitive) {
          searchPattern = null; // Will use indexOf
        } else {
          const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          searchPattern = new RegExp(escapedQuery, 'gi');
        }
      }

      // Find all matches, but skip the one we just replaced
      if (searchPattern) {
        // Using regex
        let match;
        while ((match = searchPattern.exec(newContent)) !== null) {
          const index = match.index;
          const matchedText = match[0];

          const beforeText = newContent.substring(0, index);
          const lines = beforeText.split('\n');
          const matchLine = lines.length - 1;
          const matchCol = lines[lines.length - 1].length;

          // Skip if this match is at or before our replacement position
          if (matchLine < skipUntilLine || (matchLine === skipUntilLine && matchCol < skipUntilCol)) {
            continue; // Skip this match
          }

          matches.push({
            from: { line: matchLine, ch: matchCol },
            to: { line: matchLine, ch: matchCol + matchedText.length }
          });
        }
      } else {
        // Using indexOf (case-sensitive)
        let startIndex = 0;
        while (true) {
          const index = newContent.indexOf(query, startIndex);
          if (index === -1) break;

          const beforeText = newContent.substring(0, index);
          const lines = beforeText.split('\n');
          const matchLine = lines.length - 1;
          const matchCol = lines[lines.length - 1].length;

          // Skip if this match is at or before our replacement position
          if (matchLine < skipUntilLine || (matchLine === skipUntilLine && matchCol < skipUntilCol)) {
            startIndex = index + 1;
            continue; // Skip this match
          }

          matches.push({
            from: { line: matchLine, ch: matchCol },
            to: { line: matchLine, ch: matchCol + query.length }
          });

          startIndex = index + 1;
        }
      }

      // Update counter and highlight
      currentMatch = 0; // Start at first match after replacement
      updateCounter();

      if (matches.length > 0) {
        // Highlight all matches
        matches.forEach((match, idx) => {
          const marker = cm.markText(match.from, match.to, {
            className: idx === 0 ? 'replace-highlight-active' : 'replace-highlight',
            clearOnEnter: false
          });
          replaceMarkers.push(marker);
        });

        cm.scrollIntoView(matches[0].from, 100);
      } else {
        this.showNotification('No more matches', 'info');
      }
    };

    // Highlight on input change
    findInput.addEventListener('input', () => {
      currentMatch = 0;
      highlightMatches();
    });

    // Enter key in Find input - navigate to next
    findInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Escape') {
        clearMarkers();
        replaceBox.remove();
      }
    });

    // Enter key in Replace input - replace current and go to next
    replaceInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        replaceCurrentAndNext();
      } else if (e.key === 'Escape') {
        clearMarkers();
        replaceBox.remove();
      }
    });

    // Next button - navigate to next match
    nextBtn.addEventListener('click', () => {
      goNext();
    });

    // Replace button - replace current and go to next
    replaceBox.querySelector('.search-btn-replace').addEventListener('click', () => {
      replaceCurrentAndNext();
    });

    replaceBox.querySelector('.search-btn-replace-all').addEventListener('click', () => {
      const query = findInput.value;
      const replacement = replaceInput.value;
      if (!query) return;

      // Build regex pattern based on options
      let pattern;
      if (wholeWord) {
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const flags = caseSensitive ? 'g' : 'gi';
        pattern = new RegExp(`\\b${escapedQuery}\\b`, flags);
      } else {
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const flags = caseSensitive ? 'g' : 'gi';
        pattern = new RegExp(escapedQuery, flags);
      }

      const content = cm.getValue();
      const newContent = content.replace(pattern, replacement);
      const count = (content.match(pattern) || []).length;

      if (count > 0) {
        cm.setValue(newContent);
        clearMarkers();
        matches = [];
        this.showNotification(`Replaced ${count} occurrence${count > 1 ? 's' : ''}`, 'success');
      } else {
        this.showNotification('No matches found', 'info');
      }
    });

    // Toggle buttons
    caseSensitiveBtn.addEventListener('click', () => {
      caseSensitive = !caseSensitive;
      caseSensitiveBtn.classList.toggle('active', caseSensitive);
      highlightMatches(); // Re-search with new option
    });

    wholeWordBtn.addEventListener('click', () => {
      wholeWord = !wholeWord;
      wholeWordBtn.classList.toggle('active', wholeWord);
      highlightMatches(); // Re-search with new option
    });

    replaceBox.querySelector('.search-btn-close').addEventListener('click', () => {
      clearMarkers();
      replaceBox.remove();
    });

    findInput.focus();

    // Store reference to clear markers when editor is closed
    this.currentReplaceMarkers = replaceMarkers;
  }

  formatCode(cm) {
    const code = cm.getValue();
    try {
      // Basic formatting - indent properly
      const formatted = this.beautifyCode(code);
      cm.setValue(formatted);
      this.showNotification('Code formatted successfully', 'success');
    } catch (e) {
      this.showNotification('Failed to format code: ' + e.message, 'error');
    }
  }

  beautifyCode(code) {
    // Simple beautification - add proper indentation
    const lines = code.split('\n');
    let indent = 0;
    const indentStr = '  ';

    return lines.map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '';

      // Decrease indent for closing tags
      if (trimmed.startsWith('</')) {
        indent = Math.max(0, indent - 1);
      }

      const formatted = indentStr.repeat(indent) + trimmed;

      // Increase indent for opening tags (not self-closing)
      if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>')) {
        indent++;
      }

      return formatted;
    }).join('\n');
  }

  toggleTheme() {
    const currentTheme = this.codeEditor.editor.getOption('theme');
    const newTheme = currentTheme === 'hopscotch' ? 'default' : 'hopscotch';
    this.codeEditor.editor.setOption('theme', newTheme);
    this.showNotification(`Theme changed to ${newTheme}`, 'info');
  }

  toggleFullscreen() {
    if (this.isFullscreen) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen();
    }
  }

  enterFullscreen() {
    this.savedModalSize = {
      width: this.modal.style.width,
      height: this.modal.style.height,
      left: this.modal.style.left,
      top: this.modal.style.top,
      transform: this.modal.style.transform,
    };

    this.modal.classList.add('fullscreen');
    this.modal.style.width = '100vw';
    this.modal.style.height = '100vh';
    this.modal.style.left = '0';
    this.modal.style.top = '0';
    this.modal.style.transform = 'none';

    const fullscreenBtn = this.modal.querySelector('[data-action="fullscreen"] i');
    fullscreenBtn.className = 'fa fa-compress';

    this.isFullscreen = true;
    this.codeEditor.editor.refresh();
  }

  exitFullscreen() {
    if (!this.isFullscreen) return;

    this.modal.classList.remove('fullscreen');

    if (this.savedModalSize) {
      this.modal.style.width = this.savedModalSize.width;
      this.modal.style.height = this.savedModalSize.height;
      this.modal.style.left = this.savedModalSize.left;
      this.modal.style.top = this.savedModalSize.top;
      this.modal.style.transform = this.savedModalSize.transform;
    }

    const fullscreenBtn = this.modal.querySelector('[data-action="fullscreen"] i');
    fullscreenBtn.className = 'fa fa-expand';

    this.isFullscreen = false;
    this.codeEditor.editor.refresh();
  }

  showPlaceholderHelper() {
    // Remove any existing placeholder panel (from main toolbar)
    const existingPanel = document.getElementById('placeholder-helper-panel');
    if (existingPanel) {
      existingPanel.remove();
    }

    // Create new placeholder helper with code editor mode enabled
    this.placeholderHelper = new PlaceholderHelper(this.editor, {
      codeEditorMode: true,
      codeEditor: this.codeEditor.editor
    });

    this.placeholderHelper.showPanel({
      fromCodeEditor: true,
      codeEditor: this.codeEditor.editor
    });
  }

  showHelp() {
    const helpContent = `
      <div class="code-editor-help">
        <h3>Keyboard Shortcuts</h3>
        <div class="shortcuts-grid">
          <div class="shortcut-item">
            <kbd>Ctrl/Cmd</kbd> + <kbd>F</kbd>
            <span>Find</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl/Cmd</kbd> + <kbd>H</kbd>
            <span>Replace</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl/Cmd</kbd> + <kbd>Alt</kbd> + <kbd>F</kbd>
            <span>Format Code</span>
          </div>
          <div class="shortcut-item">
            <kbd>F11</kbd>
            <span>Toggle Fullscreen</span>
          </div>
          <div class="shortcut-item">
            <kbd>Esc</kbd>
            <span>Exit Fullscreen</span>
          </div>
          <div class="shortcut-item">
            <kbd>Ctrl/Cmd</kbd> + <kbd>S</kbd>
            <span>Save Changes</span>
          </div>
        </div>
      </div>
    `;

    // Create help modal
    const helpModal = document.createElement('div');
    helpModal.className = 'code-editor-help-modal';
    helpModal.innerHTML = `
      <div class="help-modal-content">
        ${helpContent}
        <button class="code-editor-btn-primary" onclick="this.closest('.code-editor-help-modal').remove()">
          Got it!
        </button>
      </div>
    `;
    document.body.appendChild(helpModal);

    // Close on click outside
    helpModal.addEventListener('click', (e) => {
      if (e.target === helpModal) {
        helpModal.remove();
      }
    });
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `code-editor-notification notification-${type}`;
    notification.textContent = message;

    this.modal.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('show');
    }, 10);

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  // Show the enhanced editor
  show() {
    this.updateEditorContents();

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'code-editor-overlay';
    document.body.appendChild(overlay);

    // Add modal to body
    document.body.appendChild(this.modal);

    // Center modal initially
    setTimeout(() => {
      const rect = this.modal.getBoundingClientRect();
      this.modal.style.left = `${(window.innerWidth - rect.width) / 2}px`;
      this.modal.style.top = `${(window.innerHeight - rect.height) / 2}px`;
      this.modal.style.transform = 'none';
    }, 0);

    // Setup global keyboard shortcuts (backup for when CodeMirror doesn't catch them)
    this.setupGlobalKeyboardShortcuts();

    // Refresh CodeMirror after showing
    setTimeout(() => {
      this.codeEditor.editor.refresh();
      this.codeEditor.editor.focus();

      // Clear undo/redo history to prevent undo without modifications
      this.codeEditor.editor.clearHistory();

      // Jump to target component if specified
      if (this.targetComponent) {
        this.jumpToComponent(this.targetComponent);
      }
    }, 100);

    // Store references for cleanup
    this.overlay = overlay;
  }

  // Jump to specific component in code
  jumpToComponent(component) {
    if (!component || !this.codeEditor.editor) return;

    try {
      const code = this.codeEditor.editor.getValue();
      const codeLines = code.split('\n');

      // Get component identifiers
      const componentType = component.get('type');
      const componentTag = component.get('tagName');
      const componentId = component.getId();
      const componentClasses = component.getClasses().join(' ');
      const componentAttrs = component.getAttributes();

      // Get the text content to make search more specific
      const componentTextContent = component.getEl()?.textContent?.trim() || component.get('content') || '';

      // Get component's full HTML for more accurate matching
      const componentHTML = this.getComponentCode(component);
      const normalizedHTML = componentHTML ? componentHTML.trim().replace(/\s+/g, ' ') : '';

      // Build search patterns with scoring system
      // We'll find ALL potential matches and score them, then pick the best one
      const allMatches = [];

      // Search for ALL lines that contain this component's tag/type
      for (let i = 0; i < codeLines.length; i++) {
        const line = codeLines[i];
        let score = 0;
        let matchedCriteria = [];

        // Check if this line could be our component
        const hasComponentType = componentType && line.includes(`data-gjs-type="${componentType}"`);
        const hasComponentTag = componentTag && line.includes(`<${componentTag}`);

        if (!hasComponentType && !hasComponentTag) {
          continue; // Skip lines that definitely aren't our component
        }

        // SCORING: The more criteria match, the higher the score

        // Score +200: Exact HTML structure match (MOST SPECIFIC)
        // Check if the line contains a significant portion of the component's HTML
        if (normalizedHTML && normalizedHTML.length > 20) {
          const htmlSnippet = normalizedHTML.substring(0, Math.min(80, normalizedHTML.length));
          const normalizedLine = line.trim().replace(/\s+/g, ' ');
          if (normalizedLine.includes(htmlSnippet.substring(0, 50))) {
            score += 200;
            matchedCriteria.push('html-structure');
          }
        }

        // Score +100: Text content matches (IMPORTANT)
        // But only if it's unique enough (more than 3 characters)
        if (componentTextContent && componentTextContent.length > 3) {
          if (line.includes(componentTextContent)) {
            score += 100;
            matchedCriteria.push('text-content');
          }
        }

        // Score +50: Exact href matches
        if (componentAttrs.href && line.includes(componentAttrs.href)) {
          score += 50;
          matchedCriteria.push('href');
        }

        // Score +50: Exact src matches
        if (componentAttrs.src && line.includes(componentAttrs.src)) {
          score += 50;
          matchedCriteria.push('src');
        }

        // Score +40: ID matches
        if (componentId && (line.includes(`id="${componentId}"`) || line.includes(`id='${componentId}'`))) {
          score += 40;
          matchedCriteria.push('id');
        }

        // Score +20: Class matches
        if (componentClasses) {
          const firstClass = componentClasses.split(' ')[0];
          if (firstClass && line.includes(firstClass)) {
            score += 20;
            matchedCriteria.push('class');
          }
        }

        // Score +10: Component type matches
        if (hasComponentType) {
          score += 10;
          matchedCriteria.push('type');
        }

        // Score +5: Tag matches
        if (hasComponentTag) {
          score += 5;
          matchedCriteria.push('tag');
        }

        if (score > 0) {
          allMatches.push({
            line: i,
            score: score,
            criteria: matchedCriteria
          });
        }
      }

      // Sort by score (highest first) and pick the best match
      allMatches.sort((a, b) => b.score - a.score);

      let foundLine = -1;
      if (allMatches.length > 0) {
        foundLine = allMatches[0].line;
      }

      if (foundLine !== -1) {
        // Found the component! Highlight it
        const line = foundLine;

        // Try to find the end of this component's tag
        let endLine = line;
        let endColumn = codeLines[line].length;
        const startTag = componentTag || 'div';
        const endTag = `</${startTag}>`;
        const selfClosing = codeLines[line].includes('/>');

        if (!selfClosing) {
          // Count nested tags to find the CORRECT closing tag
          let depth = 1; // We're already inside one opening tag
          let searchStartPos = 0;

          // First, find where the opening tag on 'line' ends
          const openingTagMatch = codeLines[line].match(new RegExp(`<${startTag}[^>]*>`, 'i'));
          if (openingTagMatch) {
            searchStartPos = openingTagMatch.index + openingTagMatch[0].length;
          }

          // Search from the opening tag position through subsequent lines
          for (let i = line; i < Math.min(line + 50, codeLines.length); i++) {
            const searchLine = codeLines[i];
            const startFrom = (i === line) ? searchStartPos : 0;

            // Count opening and closing tags on this line
            let pos = startFrom;
            while (pos < searchLine.length) {
              // Look for opening tags
              const nextOpen = searchLine.indexOf(`<${startTag}`, pos);
              const nextClose = searchLine.indexOf(endTag, pos);

              // Determine which comes first
              if (nextClose !== -1 && (nextOpen === -1 || nextClose < nextOpen)) {
                // Found a closing tag
                depth--;
                if (depth === 0) {
                  // This is OUR closing tag!
                  endLine = i;
                  endColumn = nextClose + endTag.length;
                  break;
                }
                pos = nextClose + endTag.length;
              } else if (nextOpen !== -1) {
                // Found another opening tag (nested)
                // Make sure it's actually an opening tag, not just contains the text
                if (searchLine[nextOpen + startTag.length + 1] === ' ' ||
                    searchLine[nextOpen + startTag.length + 1] === '>') {
                  depth++;
                }
                pos = nextOpen + startTag.length + 1;
              } else {
                // No more tags on this line
                break;
              }
            }

            if (depth === 0) break;
          }
        }

        // Scroll to and highlight the component
        this.codeEditor.editor.setCursor(line, 0);

        this.codeEditor.editor.setSelection(
          { line: line, ch: 0 },
          { line: endLine, ch: endColumn }
        );

        // Scroll into view with some padding
        this.codeEditor.editor.scrollIntoView({ line: Math.max(0, line - 5), ch: 0 }, 200);

        this.showNotification(`Found ${componentType || componentTag} component at line ${line + 1}`, 'success');
      } else {
        // Fallback: just open at top
        this.codeEditor.editor.setCursor(0, 0);
        this.showNotification('Could not locate component in code', 'info');
      }
    } catch (e) {
      console.error('Failed to jump to component:', e);
      this.showNotification('Error jumping to component', 'error');
    }
  }

  getComponentCode(component) {
    try {
      // Get the component's HTML/MJML representation
      return component.toHTML();
    } catch (e) {
      return null;
    }
  }

  // Update main editor with code changes
  updateCode() {
    const code = this.codeEditor.editor.getValue();

    // Validate MJML code
    if (ContentService.isMjmlMode(this.editor)) {
      try {
        MjmlService.mjmlToHtml(code);
      } catch (e) {
        this.showNotification(`MJML Error: ${e.message}`, 'error');
        return;
      }
    }

    try {
      // Update canvas content
      this.editor.DomComponents.getWrapper().set('content', '');
      this.editor.setComponents(code.trim());

      // Persist the edit. setComponents only updates the in-memory model, NOT the
      // builder-html/builder-mjml textareas the email form actually saves. The
      // builder's Close command discards unapplied model changes (see
      // BuilderService.initCloseDiscardsChanges), so without staging here a
      // code-editor "Save" followed by closing the builder is silently lost
      // (reported by Carrie/Juncao: edits in "Edit code" never persisted).
      this.stageContentToForm();

      this.close();
      this.showNotification('Code updated successfully', 'success');
    } catch (e) {
      this.showNotification(`Syntax Error: ${e.message}`, 'error');
    }
  }

  /**
   * Write the current editor content into the builder-html/builder-mjml textareas
   * (what the email form persists on Save) and signal the builder to rebaseline its
   * close-discard snapshot, so an explicit code-editor Save survives a later Close.
   * Mirrors preset-mautic ButtonApplyCommand's staging, minus the immediate POST.
   */
  stageContentToForm() {
    if (ContentService.isMjmlMode(this.editor)) {
      const html = MjmlService.getEditorHtmlContent(this.editor);
      const mjml = MjmlService.getEditorMjmlContent(this.editor);
      if (html && mjml) {
        ButtonCloseCommands.returnContentToTextarea(this.editor, html, mjml);
      }
    } else {
      const html = ContentService.getEditorHtmlContent(this.editor);
      ButtonCloseCommands.returnContentToTextarea(this.editor, html);
    }

    // Rebaseline initCloseDiscardsChanges' snapshot to this staged content, so a
    // subsequent Close keeps the saved code instead of reverting to open-time state.
    this.editor.trigger('mautic-code-editor:applied');
  }

  // Setup global keyboard shortcuts (backup when CodeMirror doesn't catch them)
  setupGlobalKeyboardShortcuts() {
    // Store reference for cleanup
    this.keydownHandler = (e) => {
      // Only handle if modal is visible
      if (!this.modal || !document.body.contains(this.modal)) {
        return;
      }

      const cm = this.codeEditor.editor;
      if (!cm) return;

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrl = isMac ? e.metaKey : e.ctrlKey;
      const alt = e.altKey;
      const shift = e.shiftKey;
      const key = e.key.toLowerCase();

      // Ctrl/Cmd+F - Find
      if (ctrl && !alt && !shift && key === 'f') {
        e.preventDefault();
        e.stopPropagation();
        this.openSearch(cm);
        return false;
      }

      // Ctrl/Cmd+H - Replace
      if (ctrl && !alt && !shift && key === 'h') {
        e.preventDefault();
        e.stopPropagation();
        this.openReplace(cm);
        return false;
      }

      // Ctrl/Cmd+Alt+F - Format
      if (ctrl && alt && !shift && key === 'f') {
        e.preventDefault();
        e.stopPropagation();
        this.formatCode(cm);
        return false;
      }

      // Ctrl/Cmd+S - Save
      if (ctrl && !alt && !shift && key === 's') {
        e.preventDefault();
        e.stopPropagation();
        this.updateCode();
        return false;
      }

      // F11 - Fullscreen
      if (!ctrl && !alt && !shift && key === 'f11') {
        e.preventDefault();
        e.stopPropagation();
        this.toggleFullscreen();
        return false;
      }

      // Esc - Exit fullscreen
      if (!ctrl && !alt && !shift && key === 'escape') {
        if (this.isFullscreen) {
          e.preventDefault();
          e.stopPropagation();
          this.exitFullscreen();
          return false;
        }
      }
    };

    // Add to document with capture phase to catch before other handlers
    document.addEventListener('keydown', this.keydownHandler, true);
  }

  // Close editor
  close() {
    // Remove global keyboard shortcut listener
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler, true);
      this.keydownHandler = null;
    }

    if (this.modal && this.modal.parentNode) {
      this.modal.remove();
    }
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.remove();
    }

    // Notify GrapesJS
    this.editor.stopCommand('preset-mautic:code-edit');
  }

  // Update editor contents
  updateEditorContents() {
    let content;
    if (ContentService.isMjmlMode(this.editor)) {
      content = MjmlService.getEditorMjmlContent(this.editor);
      this.modal.querySelector('.file-type').textContent = 'MJML';
    } else {
      content = ContentService.getEditorHtmlContent(this.editor);
      this.modal.querySelector('.file-type').textContent = 'HTML';
    }
    this.codeEditor.setContent(content);
  }
}

export default EnhancedCodeEditor;
